/**
 * Power Monitoring Dashboard
 *
 * Displays organisation-wide power consumption overview.
 * Shows all active services with their current power levels in a tabular format.
 *
 * Issue: #13 - Instantaneous Power Monitoring
 */

import React, { useContext, useEffect, useState, useRef } from 'react';
import {
  makeStyles,
  useTheme,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  CircularProgress,
  Box,
  Tooltip,
} from '@material-ui/core';
import { FiberManualRecord as LiveIcon } from '@material-ui/icons';
import PageToolbar from '../components/pageToolbar';
import globalContext from '../context/globalContext';
import API from '../global/api';
import io from 'socket.io-client';

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    minHeight: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  tableContainer: {
    margin: 20,
  },
  powerCell: {
    fontWeight: 'bold',
    fontSize: '1.1rem',
  },
  powerHigh: {
    color: theme.palette.error.main,
  },
  powerMedium: {
    color: theme.palette.warning.main,
  },
  powerLow: {
    color: theme.palette.success.main,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(8),
  },
  noData: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  liveIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.success.light,
    borderRadius: theme.shape.borderRadius,
  },
  liveIcon: {
    color: theme.palette.success.main,
    fontSize: '0.8rem',
    animation: '$pulse 2s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.5,
    },
    '100%': {
      opacity: 1,
    },
  },
  updatedCell: {
    animation: '$highlight 1s ease-out',
  },
  '@keyframes highlight': {
    '0%': {
      backgroundColor: theme.palette.success.light,
    },
    '100%': {
      backgroundColor: 'transparent',
    },
  },
}));

const PowerMonitoring = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const context = useContext(globalContext);

  const [powerData, setPowerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLiveStreaming, setIsLiveStreaming] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const socketRef = useRef(null);
  const updatedServicesRef = useRef(new Set());

  // Initial load - get snapshot data as fallback
  const loadPowerData = async () => {
    if (typeof context.profile.data.id_organisation === 'undefined') return;

    try {
      setLoading(true);
      console.log('[POWER-MONITOR] Loading snapshot data for org:', context.profile.data.id_organisation);

      const response = await API.req(
        `/services/power/${context.profile.data.id_organisation}`,
        'GET',
        { sampling_minutes: 60 }
      );

      console.log('[POWER-MONITOR] API response:', response);

      if (response.success && response.result) {
        console.log(`[POWER-MONITOR] Loaded ${response.result.length} services`);
        setPowerData(response.result);
        setError(null);

        if (response.result.length === 0) {
          console.warn('[POWER-MONITOR] API succeeded but returned 0 services');
          setError('No active services to monitor. Start an electricity service to see power data.');
        }
      } else {
        console.error('[POWER-MONITOR] API returned success=false:', response);
        setError(response.msg || 'Failed to load power data');
      }
    } catch (err) {
      console.error('[POWER-MONITOR] API call failed:', err);
      console.error('[POWER-MONITOR] Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      });

      // Provide more specific error messages
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('Authentication failed. Please refresh the page and try again.');
      } else if (err.response?.status === 404) {
        setError('Power monitoring endpoint not found. Backend may need to be updated and restarted.');
      } else if (err.response?.status === 500) {
        setError('Server error loading power data. Check backend logs for details.');
      } else if (err.message?.includes('timeout')) {
        setError('Request timed out. Backend may be slow or unresponsive.');
      } else if (err.message?.includes('Network Error')) {
        setError('Network error. Check if backend is accessible.');
      } else {
        setError(`Failed to load snapshot data: ${err.message || 'Unknown error'}`);
      }
      // Don't show snack on initial error - real-time may still work
    } finally {
      setLoading(false);
    }
  };

  // Handle real-time power updates from Socket.IO
  const handlePowerUpdate = (data) => {
    if (data.type === 'power-update' && data.payload) {
      const update = data.payload;

      // Update or add the service in the power data
      setPowerData((prevData) => {
        const existingIndex = prevData.findIndex(
          (item) => item.id_service === update.id_service
        );

        const newData = [...prevData];

        if (existingIndex >= 0) {
          // Update existing service
          newData[existingIndex] = { ...newData[existingIndex], ...update };
        } else {
          // Add new service
          newData.push(update);
        }

        return newData;
      });

      // Track which service was updated for visual highlight
      updatedServicesRef.current.add(update.id_service);
      setTimeout(() => {
        updatedServicesRef.current.delete(update.id_service);
      }, 1000);

      setLastUpdate(new Date());
      setIsLiveStreaming(true);
      setError(null);
    }
  };

  // Setup Socket.IO connection
  useEffect(() => {
    if (typeof context.profile.data.id_organisation === 'undefined') return;

    // Initial snapshot load
    loadPowerData();

    // Connect to Socket.IO for real-time updates
    const backofficeUrl = process.env.REACT_APP_BACKOFFICE_URL || window.location.origin;
    const socket = io(backofficeUrl, {
      transports: ['websocket', 'polling'],
      auth: {
        token: context.profile.data.token
      }
    });

    socketRef.current = socket;

    // Join organisation room
    socket.emit('join', { room: `org-${context.profile.data.id_organisation}` });

    // Listen for power updates
    socket.on('message', handlePowerUpdate);

    socket.on('connect', () => {
      console.log('[POWER-MONITOR] Socket.IO connected - real-time streaming active');
      setIsLiveStreaming(true);
    });

    socket.on('disconnect', () => {
      console.log('[POWER-MONITOR] Socket.IO disconnected - falling back to snapshots');
      setIsLiveStreaming(false);
    });

    socket.on('connect_error', (err) => {
      console.error('[POWER-MONITOR] Socket.IO connection error:', err);
      setIsLiveStreaming(false);
    });

    // Cleanup on unmount
    return () => {
      socket.off('message', handlePowerUpdate);
      socket.disconnect();
    };
  }, [context.profile]);

  // Get service type from utility
  const getServiceType = (id_utility) => {
    const utility = context.utilities.data.find(
      (u) => parseInt(u.id_utility) === parseInt(id_utility)
    );
    if (!utility) return 'Unknown';

    return utility.utility_type === 'electricity' ? 'Electricity' :
           utility.utility_type === 'water' ? 'Water' :
           utility.utility_type;
  };

  // Get power limit for color coding
  const getPowerLimit = (id_utility) => {
    const utility = context.utilities.data.find(
      (u) => parseInt(u.id_utility) === parseInt(id_utility)
    );
    return utility ? utility.power_limit_kw || 5 : 5;
  };

  // Determine power cell color based on percentage of limit
  const getPowerClass = (powerKw, id_utility) => {
    if (powerKw === null || powerKw === undefined) return '';

    const limit = getPowerLimit(id_utility);
    const percentage = (powerKw / limit) * 100;

    if (percentage >= 80) return classes.powerHigh;
    if (percentage >= 50) return classes.powerMedium;
    return classes.powerLow;
  };

  // Format power value
  const formatPower = (powerKw) => {
    if (powerKw === null || powerKw === undefined) return 'No data';
    return `${powerKw.toFixed(2)} kW`;
  };

  // Get service status
  const getServiceStatus = () => {
    return 'Connected';
  };

  if (loading && powerData.length === 0) {
    return (
      <div className={classes.container}>
        <PageToolbar label="Power Monitoring" />
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <PageToolbar label="Power Monitoring" />

      {error && (
        <Box p={2}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {isLiveStreaming && (
        <Box px={2}>
          <Paper className={classes.liveIndicator}>
            <LiveIcon className={classes.liveIcon} />
            <Typography variant="body2">
              <strong>LIVE</strong> - Real-time power streaming active
              {lastUpdate && ` (last update: ${lastUpdate.toLocaleTimeString()})`}
            </Typography>
          </Paper>
        </Box>
      )}

      {powerData.length === 0 ? (
        <Typography className={classes.noData} variant="h6">
          No active services to monitor
        </Typography>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Customer</strong></TableCell>
                <TableCell><strong>Boat Name</strong></TableCell>
                <TableCell><strong>Utility ID</strong></TableCell>
                <TableCell><strong>Pedestal</strong></TableCell>
                <TableCell><strong>Service Type</strong></TableCell>
                <TableCell align="right"><strong>Instantaneous Power</strong></TableCell>
                <TableCell align="right"><strong>Current Usage (kWh)</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {powerData.map((row) => {
                const isUpdated = updatedServicesRef.current.has(row.id_service);
                const isRealtime = row.realtime === true;

                return (
                  <TableRow
                    key={row.id_service}
                    hover
                    className={isUpdated ? classes.updatedCell : ''}
                  >
                    <TableCell>{row.customer_name || 'Unknown'}</TableCell>
                    <TableCell>{row.boat_name || '-'}</TableCell>
                    <TableCell>{row.utility_identity || 'Unknown'}</TableCell>
                    <TableCell>{row.pedestal_reference || '-'}</TableCell>
                    <TableCell>{getServiceType(row.id_utility)}</TableCell>
                    <TableCell
                      align="right"
                      className={`${classes.powerCell} ${getPowerClass(row.power_kw, row.id_utility)}`}
                    >
                      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                        {formatPower(row.power_kw)}
                        {isRealtime && (
                          <Tooltip title="Real-time streaming data">
                            <LiveIcon style={{ fontSize: '0.8rem', color: theme.palette.success.main }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {row.consumption_kwh || '0.00'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getServiceStatus()}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};

export default PowerMonitoring;
